import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxList,
  ComboboxOption,
} from '@amore/ui/components/combobox'
import { Field } from '@amore/ui/components/field'
import { FormLayout } from '@amore/ui/components/form-layout'
import { useRef, useState } from 'react'
import { usePrototype } from '@/mock/store'
import { DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import brideIllustration from '../overview/assets/bride.svg'
import groomIllustration from '../overview/assets/groom.svg'

type GuestOption = { id: string; name: string }

function searchText(value: string) {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase('cs')
}

function WitnessField({
  id,
  label,
  illustration,
  guests,
  value,
  onValueChange,
  disabled,
}: {
  id: string
  label: string
  illustration: string
  guests: GuestOption[]
  value: string | null
  onValueChange: (value: string | null) => void
  disabled: boolean
}) {
  const [query, setQuery] = useState('')
  const selected = guests.find((guest) => guest.id === value)
  const matches = guests.filter((guest) => searchText(guest.name).includes(searchText(query)))

  return (
    <section className="flex items-start gap-4">
      <img
        src={illustration}
        alt=""
        width={112}
        height={120}
        className="size-16 shrink-0 rounded-full bg-panel-accent object-contain p-1"
      />
      <Field
        controlId={id}
        label={<span className="text-sm font-normal text-muted-foreground">{label}</span>}
        className="min-w-0 flex-1"
      >
        <Combobox
          name={id}
          value={value ?? 'none'}
          displayValue={selected?.name ?? (value ? 'Dříve vybraný host' : '')}
          query={query}
          onQueryChange={setQuery}
          onValueChange={(next) => onValueChange(next === 'none' ? null : next)}
          disabled={disabled}
        >
          <ComboboxInput
            variant="soft"
            className="h-10 border-transparent bg-muted/60 hover:bg-muted"
            placeholder="Zatím nevybráno"
          />
          <ComboboxContent>
            <ComboboxList>
              {!query ? <ComboboxOption value="none">Zatím nevybráno</ComboboxOption> : null}
              {matches.map((guest) => (
                <ComboboxOption key={guest.id} value={guest.id}>
                  {guest.name}
                </ComboboxOption>
              ))}
              {!matches.length ? (
                <ComboboxEmpty>
                  {guests.length ? 'Žádný host neodpovídá hledání.' : 'Zatím nemáte žádné hosty.'}
                </ComboboxEmpty>
              ) : null}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
      </Field>
    </section>
  )
}

export function CeremonyWitnessesSheet({ onClose }: { onClose: () => void }) {
  const { data, canEdit, update } = usePrototype()
  const { showToast } = useToast()
  const [brideWitnessId, setBrideWitnessId] = useState(data.ceremony.brideWitnessId)
  const [groomWitnessId, setGroomWitnessId] = useState(data.ceremony.groomWitnessId)
  const formRef = useRef<HTMLFormElement>(null)
  const guests = data.parties.flatMap((party) =>
    party.persons.map((guest) => ({
      id: guest.id,
      name: `${guest.firstName} ${guest.lastName}`.trim() || 'Doprovod',
    })),
  )
  const dirty =
    canEdit &&
    (brideWitnessId !== data.ceremony.brideWitnessId ||
      groomWitnessId !== data.ceremony.groomWitnessId)

  return (
    <DetailSheet
      open
      eyebrow="Lidé a role"
      title="Svědci"
      dirty={dirty}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      primaryAction={
        canEdit ? { label: 'Uložit', onClick: () => formRef.current?.requestSubmit() } : undefined
      }
    >
      <FormLayout
        ref={formRef}
        onSubmit={(event) => {
          event.preventDefault()
          if (!canEdit) return
          update((current) => ({
            ...current,
            ceremony: { ...current.ceremony, brideWitnessId, groomWitnessId },
          }))
          showToast({ title: 'Svědci uloženi', tone: 'success' })
          onClose()
        }}
        className="divide-y divide-border space-y-0 [&>*]:py-7 [&>*:first-child]:pt-1 [&>*:last-child]:pb-1"
      >
        <WitnessField
          id="ceremony-bride-witness"
          label={
            data.identity.brideName
              ? `Svědek · ${data.identity.brideName}`
              : 'Svědek prvního z páru'
          }
          illustration={brideIllustration}
          guests={guests}
          value={brideWitnessId}
          onValueChange={setBrideWitnessId}
          disabled={!canEdit}
        />
        <WitnessField
          id="ceremony-groom-witness"
          label={
            data.identity.groomName
              ? `Svědek · ${data.identity.groomName}`
              : 'Svědek druhého z páru'
          }
          illustration={groomIllustration}
          guests={guests}
          value={groomWitnessId}
          onValueChange={setGroomWitnessId}
          disabled={!canEdit}
        />
      </FormLayout>
    </DetailSheet>
  )
}
